import React, { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { X, Check, Pencil } from "lucide-react";
import { cn } from "@/lib/utils";

interface InlineEditableInputProps {
  value: string;
  onSave: (value: string) => void;
  onCancel?: () => void;
  onEditStateChange?: (isEditing: boolean) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  showEditIcon?: boolean;
  isEditing?: boolean; // Add external control prop
  maxLength?: number;
}

export const InlineEditableInput: React.FC<InlineEditableInputProps> =
  React.memo(
    ({
      value,
      onSave,
      onCancel,
      onEditStateChange,
      placeholder = "",
      className = "",
      disabled = false,
      showEditIcon = true,
      isEditing: externalIsEditing,
      maxLength,
    }) => {
      const [internalIsEditing, setInternalIsEditing] = useState(false);
      const [inputValue, setInputValue] = useState(value);

      // Use external control if provided, otherwise use internal state
      const isEditing =
        externalIsEditing !== undefined ? externalIsEditing : internalIsEditing;

      useEffect(() => {
        setInputValue(value);
      }, [value]);

      useEffect(() => {
        onEditStateChange?.(isEditing);
      }, [isEditing, onEditStateChange]);

      const handleSave = () => {
        if (inputValue.trim()) {
          onSave(inputValue.trim());
          if (externalIsEditing !== undefined) {
            onEditStateChange?.(false);
          } else {
            setInternalIsEditing(false);
          }
        }
      };

      const handleCancel = () => {
        setInputValue(value);
        if (externalIsEditing !== undefined) {
          onEditStateChange?.(false);
        } else {
          setInternalIsEditing(false);
        }
        onCancel?.();
      };

      const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === "Enter") {
          e.preventDefault();
          e.stopPropagation();
          handleSave();
        } else if (e.key === "Escape") {
          e.preventDefault();
          e.stopPropagation();
          handleCancel();
        }
      };

      if (isEditing) {
        return (
          <div
            className={cn(
              "flex gap-2 items-center pt-0 w-full border ml-0.5 pr-1 border-transparent hover:border-secondary-n-600 focus-within:border-primary-0! rounded-md outline-hidden justify-between bg-secondary-n-950",
              className,
            )}
          >
            <Input
              variant="unstyled"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={placeholder}
              disabled={disabled}
              maxLength={maxLength}
              className="w-full h-8 px-2 font-normal text-xs justify-between border-none! outline-hidden ring-0 focus:ring-0 focus:outline-hidden bg-transparent! focus:bg-transparent placeholder:text-secondary-n-600"
              autoFocus
            />
            <div className="flex items-center gap-2 pr-1">
              <X
                onClick={handleCancel}
                className="size-3.5 text-secondary-n-600 hover:text-secondary-n-50 cursor-pointer"
              />
              <Check
                onClick={handleSave}
                className="size-3.5 text-secondary-n-600 hover:text-secondary-n-50 cursor-pointer"
              />
            </div>
          </div>
        );
      }

      return (
        <div className="flex items-center gap-2">
          <span className="text-2xs font-medium text-secondary-n-50 leading-3">
            {value || placeholder}
          </span>
          {showEditIcon && !disabled && (
            <Pencil
              onClick={() => {
                if (externalIsEditing !== undefined) {
                  onEditStateChange?.(true);
                } else {
                  setInternalIsEditing(true);
                }
              }}
              className="w-4 h-4 text-secondary-n-600 hidden group-hover:flex hover:text-secondary-n-50 cursor-pointer"
            />
          )}
        </div>
      );
    },
  );

InlineEditableInput.displayName = "InlineEditableInput";
